import { Box, VStack } from "@chakra-ui/react-v3";
import Chart from "react-apexcharts";
import dayjs from "dayjs";
import { ISO_DATE } from "@metriport/shared/common/date";
import { BaseSeries } from "../table-transforms/series-utils";

export type ChartSeriesData = {
  name: string;
  data: Array<{ date: string; value: number }>;
  color?: string;
};

export type SeriesChartProps = {
  series: BaseSeries<unknown>;
  chartSeries: ChartSeriesData[];
  yAxisLabel?: string;
  height?: number;
  width?: number;
};

export function SeriesChart({
  series,
  chartSeries,
  yAxisLabel,
  height = 320,
  width = 500,
}: SeriesChartProps) {
  const allChartData = chartSeries.map(seriesData => {
    const chartData = seriesData.data
      .map(point => ({
        x: dayjs(point.date).format(ISO_DATE),
        y: point.value,
        date: dayjs(point.date).toDate().getTime(),
      }))
      .sort((a, b) => a.date - b.date);

    return { ...seriesData, chartData };
  });

  const hasAnyData = allChartData.some(s => s.chartData.length > 0);

  if (!hasAnyData) {
    return (
      <Box w="100%" p={4} textAlign="center" color="fg.subtle">
        No numeric data available for charting
      </Box>
    );
  }

  const allValues = allChartData.flatMap(s => s.chartData.map(point => point.y));
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);

  const range = maxValue - minValue;
  const padding = range > 0 ? range * 0.1 : Math.abs(minValue) * 0.1 || 1;
  const yAxisMin = minValue - padding;
  const yAxisMax = maxValue + padding;

  const apexChartSeries = allChartData.map(s => ({
    name: s.name,
    data: s.chartData.map(point => ({
      x: point.x,
      y: point.y,
    })),
    color: s.color,
  }));

  return (
    <Box w="100%">
      <VStack align="start">
        <Chart
          options={{
            chart: {
              id: `${series.code}-chart`,
              type: "line",
              toolbar: {
                show: true,
              },
            },
            colors: apexChartSeries.map(s => s.color).filter(Boolean) as string[],
            xaxis: {
              type: "datetime",
              title: {
                text: "Date",
              },
            },
            yaxis: {
              min: yAxisMin,
              max: yAxisMax,
              decimalsInFloat: 2,
              title: {
                text: yAxisLabel || "Value",
              },
            },
            dataLabels: {
              enabled: false,
            },
            tooltip: {
              enabled: true,
              x: {
                format: "MMM dd, yyyy",
              },
            },
            markers: {
              size: 4,
              hover: {
                size: 6,
              },
            },
            stroke: {
              curve: "smooth",
              width: 2,
            },
            grid: {
              show: true,
              strokeDashArray: 3,
            },
          }}
          series={apexChartSeries}
          type="line"
          width={width}
          height={height}
        />
      </VStack>
    </Box>
  );
}
